// Sessão da CONTA SWIPE (swipeuser) persistente no app TV — base do sync
// cross-device. Plan: docs/plan/sync-conta-cross-device.md (S3.0).
//
// CONTEXTO: até aqui o app TV usava o token swipeuser SÓ no pareamento QR e o
// DESCARTAVA (seguia só com o JWT Xtream do auth-store). Pro sync funcionar de
// forma contínua ("continuar de onde parou" ao reabrir o app), a sessão Swipe
// precisa SOBREVIVER ao reload: persistimos o refresh (30d) e renovamos o access
// (15min) sob demanda — espelha o padrão do auth-store (Xtream), mas em chave
// separada e usando /v1/swipe-account/refresh (rotation atômica do backend).
//
// SEGURANÇA: é o mesmo nível do token Xtream (já em localStorage). O refresh é
// rotacionado a cada uso pelo backend (refresh roubado tem janela curta). Sem
// localStorage (SSR/teste) → no-op silencioso (sync só não liga).

import { getApiBase } from '../api'
import { orFallbackAfter } from '../lib/deadline'

import { SYNC_REQUEST_TIMEOUT_MS } from './net'

export interface SwipeSession {
  accessToken: string
  refreshToken: string
  accessExpiresAt: number // unix sec
  swipeUserId: string
}

const STORAGE_KEY = 'iptv:swipe-session'
const ACCESS_TTL_SEC = 15 * 60 // bate com o backend (signAccessToken 15m)

let session: SwipeSession | null = null
let loaded = false

/**
 * A GERAÇÃO da sessão (AP-116, sondas R5 do reviewer e P3 do cybersec): muda a cada
 * pareamento e a cada fim de sessão (sign-out, morte do refresh), e NÃO muda na rotação
 * do token. É a identidade da SESSÃO, não da conta: sign-out seguido de novo pareamento
 * da mesma conta é outra geração. Quem começa uma operação longa numa sessão (o flush,
 * o sign-out com flush final) confere a geração depois de cada `await`. Só em memória:
 * nenhuma operação atravessa um reload.
 */
let sessionGeneration = 0

/** A geração da sessão atual (ver `sessionGeneration`). */
export function getSessionGeneration(): number {
  return sessionGeneration
}

function load(): SwipeSession | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as SwipeSession
    if (
      typeof p.accessToken === 'string' &&
      typeof p.refreshToken === 'string' &&
      typeof p.swipeUserId === 'string' &&
      typeof p.accessExpiresAt === 'number'
    ) {
      return p
    }
  } catch {
    // corrompido → ignora
  }
  return null
}

/** Chaves de localStorage SACRIFICÁVEIS — caches/cosméticos que regeneram
 *  sozinhos. Se o disco recusar a gravação da sessão (quota apertada no Tizen
 *  2017), liberamos ESTAS pra abrir espaço e tentar de novo. NUNCA entra aqui
 *  nada crítico (sessão, token, credencial lembrada, o marcador de vínculo). */
const SACRIFICIAL_KEYS = ['iptv:search-history', 'iptv:long-session-dismissed-at'] as const

function evictSacrificial(): void {
  for (const k of SACRIFICIAL_KEYS) {
    try {
      localStorage.removeItem(k)
    } catch {
      // best-effort — se nem remover dá, seguimos pro retry mesmo assim.
    }
  }
}

/** Grava + READ-BACK VERIFY: só devolve true se o disco confirma EXATAMENTE o
 *  que gravamos. Um `setItem` que "não lança" mas não persiste (visto em
 *  WebViews antigos sob pressão de quota) mentiria como sucesso; a releitura é a
 *  única prova real de durabilidade. Robusto de propósito (sem checar
 *  `DOMException.code`, que varia em Chr38/53): qualquer falha vira false. */
function writeBack(serialized: string): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, serialized)
  } catch {
    return false
  }
  try {
    return localStorage.getItem(STORAGE_KEY) === serialized
  } catch {
    return false
  }
}

/**
 * Persiste a sessão no disco. Retorna `persisted`: true só se a gravação foi
 * CONFIRMADA por read-back. Em falha (quota provável) libera caches sacrificáveis
 * e tenta MAIS UMA vez. NÃO lança nunca — quem chama honra o boolean.
 *
 * BLINDAGEM DEFENSIVA, não a causa raiz: o estouro real de quota que motivou isto
 * era o IDB do catálogo (15k títulos), não esta chave (poucos bytes). Aqui é rede
 * de segurança + sinal honesto; quem GARANTE a recuperação da sessão é o ramo de
 * 401 do `refreshAccess`.
 */
function save(s: SwipeSession | null): boolean {
  if (typeof localStorage === 'undefined') return false
  if (s === null) {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // best-effort; não há durabilidade a garantir numa remoção.
    }
    return true
  }
  const serialized = JSON.stringify(s)
  if (writeBack(serialized)) return true
  // 1ª gravação não confirmou → abre espaço e tenta de novo (single retry).
  evictSacrificial()
  return writeBack(serialized)
}

function current(): SwipeSession | null {
  if (!loaded) {
    session = load()
    loaded = true
  }
  return session
}

/** Há uma conta Swipe logada (com refresh válido em disco)? */
export function hasSwipeSession(): boolean {
  return current() !== null
}

/** Id da conta Swipe logada (pra o sync escopar). null se deslogado. */
export function getSwipeUserId(): string | null {
  return current()?.swipeUserId ?? null
}

/**
 * Comita a sessão Swipe em MEMÓRIA e tenta persistir no disco. Retorna
 * `persisted` (a gravação foi confirmada por read-back?). A memória é SEMPRE
 * atualizada — mesmo com disco cheio a sessão atual segue viva; o boolean diz se
 * ela vai SOBREVIVER ao reload. `expiresInSec` default 15min (TTL do access).
 *
 * Contrato do retorno por chamador:
 *  - ROTAÇÃO (refreshAccess): ignora-se um `false` a ponto de derrubar a sessão —
 *    o token novo já está em memória e a sessão em curso continua; só telemetria.
 *  - PAREAMENTO/LOGIN (via setSwipeSessionFromPairing): `false` = não durável →
 *    faz rollback e avisa o usuário (não entra no app com sessão só-em-memória,
 *    que recriaria o bug "cai no gate a cada boot").
 */
export function setSwipeSession(args: {
  accessToken: string
  refreshToken: string
  swipeUserId: string
  expiresInSec?: number
}): boolean {
  const before = current()?.swipeUserId ?? null
  session = {
    accessToken: args.accessToken,
    refreshToken: args.refreshToken,
    swipeUserId: args.swipeUserId,
    accessExpiresAt: Math.floor(Date.now() / 1000) + (args.expiresInSec ?? ACCESS_TTL_SEC),
  }
  loaded = true
  const persisted = save(session)
  notifyIdentity(before, args.swipeUserId)
  return persisted
}

/** Desloga a conta Swipe (limpa disco + memória). Chamado no logout/troca de conta. */
export function clearSwipeSession(): void {
  const before = current()?.swipeUserId ?? null
  session = null
  loaded = true
  sessionGeneration++
  save(null)
  notifyIdentity(before, null)
}

// ── Quem é a conta da sessão MUDOU (cybersec P1, 10/10/2026) ──
// A sessão (aqui) e o contexto de conta (store) morriam em momentos diferentes: o 401 do
// refresh chega só a `clearSwipeSession`, e o contexto de A seguia no store pra conta B
// herdar. Quem precisa reagir à troca de identidade (context-owner.ts) assina aqui e é
// avisado de forma SÍNCRONA, no mesmo tick da troca, antes de qualquer render. Por
// assinatura e não por import, pra esta camada não depender do store.
type IdentityListener = (swipeUserId: string | null) => void
const identityListeners = new Set<IdentityListener>()

/** Avisa a cada troca da conta da sessão (inclusive pra `null`). Devolve o unsubscribe. */
export function onSwipeIdentityChange(listener: IdentityListener): () => void {
  identityListeners.add(listener)
  return () => {
    identityListeners.delete(listener)
  }
}

function notifyIdentity(before: string | null, after: string | null): void {
  if (before === after) return
  for (const l of identityListeners) {
    try {
      l(after)
    } catch (err) {
      console.warn('[sync] listener de identidade falhou:', err)
    }
  }
}

/**
 * Extrai o `swipeUserId` do payload de um access token (decode do JWT SEM
 * verificar assinatura — só pra bucketing/escopo; a autorização real é no
 * backend). Retorna null se o token não tiver o claim ou não for decodificável.
 */
export function extractSwipeUserId(accessToken: string): string | null {
  try {
    const part = accessToken.split('.')[1]
    if (!part) return null
    // base64url → JSON
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json) as { swipeUserId?: string; type?: string }
    if (payload.type === 'swipeuser' && typeof payload.swipeUserId === 'string') {
      return payload.swipeUserId
    }
    return null
  } catch {
    return null
  }
}

/**
 * Persiste a sessão Swipe a partir do par de tokens do pareamento/login aprovado,
 * extraindo o swipeUserId do access. Conveniência pro fluxo de QR e do
 * account-auth. Retorna `persisted` (durou no disco?):
 *  - false se o token não tiver swipeUserId (não persiste — sync não liga sem id).
 *  - false se o disco recusou a gravação (quota) → faz ROLLBACK da memória e
 *    devolve false, pra o caller não entrar no app com uma sessão que some no
 *    reload. Em ambos os casos o disco fica limpo desta chave.
 */
export function setSwipeSessionFromPairing(accessToken: string, refreshToken: string): boolean {
  const swipeUserId = extractSwipeUserId(accessToken)
  if (!swipeUserId) return false
  const persisted = setSwipeSession({ accessToken, refreshToken, swipeUserId })
  // Um pareamento é uma sessão NOVA, mesmo que da mesma conta.
  sessionGeneration++
  if (!persisted) {
    // Não conseguimos GARANTIR durabilidade. Desfaz a memória: melhor falhar
    // visível e retryável do que prender o usuário no gate a cada boot seguinte
    // (o mesmo invariante que o boot exige: "só entra quem tem sessão em disco").
    clearSwipeSession()
    return false
  }
  return true
}

function accessExpired(skewSec = 60): boolean {
  const s = current()
  if (!s) return true
  return Math.floor(Date.now() / 1000) + skewSec >= s.accessExpiresAt
}

/**
 * O voo de refresh em andamento: UM por refresh token. Dois voos com o mesmo token são
 * logout certo (o servidor consome o token na chegada e o segundo toma 401). O `token`
 * acompanha a adoção do token do disco na recuperação do 401 (ver abaixo), pra quem
 * chega no meio entrar no mesmo voo.
 */
let refreshFlight: { token: string; done: Promise<string | null> } | null = null

/**
 * Renova o access usando o refresh (rotation no backend). Idempotente: chamadas
 * concorrentes compartilham 1 request inflight. Em falha de REDE → mantém a
 * sessão (hiccup transitório, tenta de novo depois). Retorna o access novo ou null.
 *
 * ===== 401 NÃO é sempre terminal (recuperação client-only) =====
 * Um 401 no refresh pode ser um FALSO-TERMINAL: a conta está VIVA, mas o token
 * que apresentamos foi consumido por uma ROTAÇÃO CONCORRENTE que pousou no disco
 * um refresh novo e válido (ex.: outro contexto renovou entre a leitura da
 * memória e este request). Limpar cego aqui prende um usuário legítimo no gate de
 * QR, igual a uma conta revogada.
 *
 * Então, no 401, decidimos 100% no cliente (sem ler nenhum campo do backend):
 *  1. Re-lê o refresh DO DISCO (`load()`, ignora o cache em memória).
 *  2. Se o disco tem um token DIFERENTE, MAIS NOVO e da MESMA conta → uma rotação
 *     concorrente pousou token válido → RETRY ÚNICO (single-shot) com o token do
 *     disco. O token vem SÓ do disco, nunca é fabricado a partir do 401.
 *  3. Senão (mesmo/mais velho/OUTRA conta) → o refresh morreu de fato:
 *     `clearSwipeSession()`. O marcador `iptv:swipe-linked` mora em outra chave e
 *     SOBREVIVE → o gate mostra "reconecte", não o gate frio.
 *
 * SINGLE-SHOT (bounded=1): o `refreshFlight` só de-dupa concorrentes, não limita
 * retries sequenciais. O guard `recoveryTried` garante NO MÁXIMO um retry de
 * recuperação por invocação — um 401 "recuperável" persistente não vira loop.
 *
 * CUIDADO (ressalva do cybersec): a comparação é por `swipeUserId` igual E token
 * mais novo, NUNCA só "bytes diferentes" — se o usuário logou noutra conta Swipe
 * no mesmo device, o disco tem token de OUTRO usuário, e retentar com ele
 * refrescaria a sessão errada.
 *
 * ===== O PRAZO limita a ESPERA de quem chamou, nunca o voo (AP-116, sonda R1) =====
 * O servidor consome o refresh apresentado assim que ele CHEGA (rotação com
 * reuse-detection). Um prazo que abandonasse o voo perdia a resposta com o par novo, o
 * cliente ficava com o refresh já consumido, e o refresh seguinte tomava 401: logout
 * espúrio (medido: `[200, 401]`). Então: o voo não tem prazo nem `signal`, e a resposta
 * é aplicada quando chegar, mesmo depois de quem chamou ter desistido; quem chama espera
 * no máximo SYNC_REQUEST_TIMEOUT_MS (por corrida) e segue sem token. Um voo por token
 * (`refreshFlight`): quem chega com o voo no ar entra nele, nunca abre outro com o mesmo
 * token. Um voo que nunca volta deixa o refresh daquele token parado no run (a resposta
 * não pode ser dada por perdida: se o servidor já rotacionou, tentar de novo com o token
 * velho é o 401 certo).
 *
 * ===== A resposta só vale pra MESMA SESSÃO (sonda R2 e P3 do cybersec) =====
 * Se a sessão mudou com o voo no ar (sign-out, outra conta pareou, ou a MESMA conta
 * pareou de novo), o refresh apresentado não é mais o da sessão, e o resultado é
 * descartado: gravá-lo ressuscitava a sessão depois do sign-out, ou sobrescrevia o par
 * novo. A comparação é pelo refresh token apresentado, não pela conta: um par novo da
 * mesma conta é outra sessão.
 */
async function refreshAccess(): Promise<string | null> {
  const s = current()
  if (!s) return null
  if (!refreshFlight || refreshFlight.token !== s.refreshToken) {
    const flight: { token: string; done: Promise<string | null> } = {
      token: s.refreshToken,
      done: Promise.resolve(null),
    }
    flight.done = runRefresh(s, flight)
    refreshFlight = flight
    void flight.done.then(() => {
      if (refreshFlight === flight) refreshFlight = null
    })
  }
  // O prazo limita só a espera de quem chamou (ver o cabeçalho).
  return orFallbackAfter(refreshFlight.done, SYNC_REQUEST_TIMEOUT_MS, null)
}

/**
 * Os códigos com que o servidor diz que o refresh MORREU de fato. O `POST
 * /v1/swipe-account/refresh` responde hoje `401 { error: 'invalid_refresh' }` pra token
 * revogado, reusado, vencido ou de conta bloqueada (apps/api/src/routes/swipe-account.ts;
 * o teste lê aquele arquivo). Os demais nomes ficam na lista pra o dia em que o servidor
 * os separar, sem precisar de outro deploy do cliente.
 */
export const DEFINITIVE_REFRESH_ERRORS: ReadonlySet<string> = new Set([
  'invalid_refresh',
  'session_revoked',
  'token_reused',
  'account_blocked',
  'account_suspended',
])

/**
 * A resposta do refresh é uma revogação DEFINITIVA da sessão?
 *
 * ── A regra (decisão do dono, 10/10/2026) ──
 * Sessão revogada derruba na hora, inclusive no meio do vídeo (a fronteira do app
 * reage e manda pro gate de reconexão). Mas SÓ revogação definitiva: 401/403 COM um
 * código de `DEFINITIVE_REFRESH_ERRORS`. Erro de rede, prazo, 5xx, 429, API fora do ar e
 * um 401/403 sem código conhecido (um proxy, a borda) NUNCA derrubam: a sessão segue, o
 * vídeo segue, e o sync tenta de novo depois. Errar pro lado de manter custa um refresh a
 * mais; errar pro lado de derrubar custa o usuário no meio do filme.
 */
export function isDefinitiveRevocation(status: number, errorCode: string | null): boolean {
  if (status !== 401 && status !== 403) return false
  return errorCode !== null && DEFINITIVE_REFRESH_ERRORS.has(errorCode)
}

/** O `error` do corpo JSON de uma resposta de recusa, ou null (corpo vazio/HTML/ilegível). */
async function readErrorCode(r: { json: () => Promise<unknown> }): Promise<string | null> {
  try {
    const body = (await r.json()) as { error?: unknown } | null
    return typeof body?.error === 'string' ? body.error : null
  } catch {
    return null
  }
}

/** O voo do refresh (ver `refreshAccess`). Nunca rejeita. */
async function runRefresh(s: SwipeSession, flight: { token: string }): Promise<string | null> {
  // Token/baseline que este request apresenta. Podem AVANÇAR uma vez, se a
  // recuperação adotar o token do disco (rotação concorrente).
  let presentedRefresh = s.refreshToken
  let baselineExpiresAt = s.accessExpiresAt
  let recoveryTried = false
  try {
    for (;;) {
      // SEM prazo e SEM signal (ver o cabeçalho de `refreshAccess`).
      const r = await fetch(`${getApiBase()}/v1/swipe-account/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: presentedRefresh }),
      })
      const res = {
        status: r.status,
        ok: r.ok,
        body: r.ok ? ((await r.json()) as { accessToken: string; refreshToken: string }) : null,
      }
      // O código do erro, quando a resposta é de recusa (ver `isDefinitiveRevocation`).
      const errorCode = r.ok ? null : await readErrorCode(r)
      // A SESSÃO mudou com o voo no ar: o refresh apresentado não é mais o dela.
      const now = current()
      if (!now || now.refreshToken !== presentedRefresh) return null
      // SÓ revogação DEFINITIVA chega à limpeza (decisão do dono, 10/10/2026). Um 401/403
      // sem um desses códigos (proxy, borda, API no meio de um deploy) cai no ramo
      // transitório abaixo: a sessão segue e o vídeo também.
      if (isDefinitiveRevocation(res.status, errorCode)) {
        const disk = load() // disco cru: bypassa o cache em memória
        if (disk && disk.swipeUserId !== s.swipeUserId) {
          // ISOLAMENTO DE TENANT: o disco é de OUTRA conta — o usuário logou
          // noutra conta Swipe no mesmo device (u2), e este request é uma
          // operação STALE da conta anterior (u1). NÃO limpamos a sessão de u2
          // (seria um write DESTRUTIVO cross-account, apagando uma sessão que
          // acabou de se estabelecer legitimamente) NEM adotamos u2 na memória
          // (trocar a conta ativa no meio da operação empurraria o outbox de u1
          // sob o token de u2 = write cross-account). A memória segue u1 (token
          // morto → nenhum request), o disco de u2 fica intacto e auto-cura no
          // reload. Fail-safe sem violar isolamento (fix P2 do cybersec).
          return null
        }
        if (
          !recoveryTried &&
          disk &&
          disk.swipeUserId === s.swipeUserId && // MESMA conta (garantido pelo early-return acima)
          disk.refreshToken !== presentedRefresh && // token diferente do que tomou 401
          disk.accessExpiresAt >= baselineExpiresAt // e MAIS NOVO (não stale/velho)
        ) {
          // Rotação concorrente venceu: adota o token do disco como verdade e
          // tenta de novo UMA vez. `recoveryTried` fecha a porta pra loop.
          recoveryTried = true
          session = disk
          loaded = true
          presentedRefresh = disk.refreshToken
          baselineExpiresAt = disk.accessExpiresAt
          flight.token = disk.refreshToken
          continue
        }
        // Morte de fato → limpa a SESSÃO. Só chega aqui com disco NULL ou disco
        // da MESMA conta com token igual/mais velho (nunca de outra conta: o
        // early-return acima já cobriu). O marcador de vínculo fica (outra
        // chave) e o gate cai na variante "reconecte".
        clearSwipeSession()
        return null
      }
      if (!res.ok || !res.body) {
        // Erro transitório (5xx do proxy num redeploy da API, 429 do limite de taxa, 401/403
        // sem código de revogação): a sessão segue. O access atual só serve se ainda não venceu (a renovação
        // começa 60s antes). Vencido, o pedido sairia com ele, tomaria 401, e o api.ts
        // deslogaria a UI com a sessão viva no disco (review de 29/09/2026). Sem token, o
        // pedido autenticado não sai e quem chamou vê falha de rede (swipe-bearer-rule.ts).
        return Math.floor(Date.now() / 1000) < now.accessExpiresAt ? now.accessToken : null
      }
      const body = res.body
      const persisted = setSwipeSession({
        accessToken: body.accessToken,
        refreshToken: body.refreshToken,
        swipeUserId: s.swipeUserId,
      })
      if (!persisted) {
        // Rotação: o disco recusou (quota). O token NOVO já está em memória — a
        // sessão em curso segue viva. NÃO limpamos (derrubaria algo que
        // funciona); só sinalizamos pra telemetria de device.
        console.warn(
          '[swipe-session] rotação não persistiu no disco (quota?); mantendo em memória',
        )
      }
      return body.accessToken
    }
  } catch {
    // rede caiu → NÃO desloga; mantém a sessão pra tentar depois.
    return null
  }
}

/**
 * Retorna um access token VÁLIDO da conta Swipe (renova se expirou). null se
 * não há sessão OU o refresh morreu. Usado pela camada de sync antes de cada
 * chamada autenticada ao backend.
 */
export async function getValidSwipeAccessToken(): Promise<string | null> {
  const s = current()
  if (!s) return null
  if (!accessExpired()) return s.accessToken
  return refreshAccess()
}
